import { resolveDateOnly, resolveDateTime } from '../relativeDates';
import { escXml } from './esc';

export interface Camt056Input {
  assignmentId: string;
  caseId: string;
  createdAt: string;
  assignorBic: string;
  assigneeBic: string;
  orgnlMsgId: string;
  orgnlMsgNmId: string;
  orgnlEndToEndId: string;
  orgnlTxId: string;
  orgnlAmount: string;
  orgnlCurrency: string;
  orgnlSettlementDate: string;
  cxlId: string;
  cxlReason: string;
  addtlInf: string;
  xmlns: string;
  /** Document xmlns for the paired camt.029 */
  pairXmlns: string;
  resolutionId: string;
  cxlStsId: string;
}

const today = new Date().toISOString().slice(0, 10);
const created = new Date().toISOString().replace(/\.\d{3}Z$/, '');

export const DEFAULT_CAMT056_INPUT: Camt056Input = {
  assignmentId: 'CXL-TRY-0001',
  caseId: 'CASE-TRY-0001',
  createdAt: created,
  assignorBic: 'DEMOFRPPXXX',
  assigneeBic: 'COBADEFFXXX',
  orgnlMsgId: 'PACS8-TRY-0001',
  orgnlMsgNmId: 'pacs.008.001.08',
  orgnlEndToEndId: 'E2E-TRY-2026-0001',
  orgnlTxId: 'TX-TRY-00000001',
  orgnlAmount: '1250.00',
  orgnlCurrency: 'EUR',
  orgnlSettlementDate: today,
  cxlId: 'CXLID-TRY-0001',
  cxlReason: 'DUPL',
  addtlInf: 'Duplicate submission after client retry',
  xmlns: 'urn:iso:std:iso:20022:tech:xsd:camt.056.001.08',
  pairXmlns: 'urn:iso:std:iso:20022:tech:xsd:camt.029.001.09',
  resolutionId: 'RSL-TRY-0001',
  cxlStsId: 'CXLSTS-TRY-0001',
};

export function freshCamt056Input(): Camt056Input {
  return {
    ...DEFAULT_CAMT056_INPUT,
    createdAt: resolveDateTime('now'),
    orgnlSettlementDate: resolveDateOnly('today'),
  };
}

export function buildCamt056(input: Camt056Input): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="${escXml(input.xmlns)}">
  <FIToFIPmtCxlReq>
    <Assgnmt>
      <Id>${escXml(input.assignmentId)}</Id>
      <Assgnr><Agt><FinInstnId><BICFI>${escXml(input.assignorBic)}</BICFI></FinInstnId></Agt></Assgnr>
      <Assgne><Agt><FinInstnId><BICFI>${escXml(input.assigneeBic)}</BICFI></FinInstnId></Agt></Assgne>
      <CreDtTm>${escXml(input.createdAt)}</CreDtTm>
    </Assgnmt>
    <Case>
      <Id>${escXml(input.caseId)}</Id>
      <Cretr><Agt><FinInstnId><BICFI>${escXml(input.assignorBic)}</BICFI></FinInstnId></Agt></Cretr>
    </Case>
    <Undrlyg>
      <TxInf>
        <CxlId>${escXml(input.cxlId)}</CxlId>
        <OrgnlGrpInf>
          <OrgnlMsgId>${escXml(input.orgnlMsgId)}</OrgnlMsgId>
          <OrgnlMsgNmId>${escXml(input.orgnlMsgNmId)}</OrgnlMsgNmId>
        </OrgnlGrpInf>
        <OrgnlEndToEndId>${escXml(input.orgnlEndToEndId)}</OrgnlEndToEndId>
        <OrgnlTxId>${escXml(input.orgnlTxId)}</OrgnlTxId>
        <OrgnlIntrBkSttlmAmt Ccy="${escXml(input.orgnlCurrency)}">${escXml(input.orgnlAmount)}</OrgnlIntrBkSttlmAmt>
        <OrgnlIntrBkSttlmDt>${escXml(input.orgnlSettlementDate)}</OrgnlIntrBkSttlmDt>
        <CxlRsnInf>
          <Rsn><Cd>${escXml(input.cxlReason)}</Cd></Rsn>
          <AddtlInf>${escXml(input.addtlInf)}</AddtlInf>
        </CxlRsnInf>
      </TxInf>
    </Undrlyg>
  </FIToFIPmtCxlReq>
</Document>`;
}

export function buildCamt029(input: Camt056Input, outcome: string): string {
  const conf = outcome;
  const txSts = outcome;
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="${escXml(input.pairXmlns)}">
  <RsltnOfInvstgtn>
    <Assgnmt>
      <Id>${escXml(input.resolutionId)}</Id>
      <Assgnr><Agt><FinInstnId><BICFI>${escXml(input.assigneeBic)}</BICFI></FinInstnId></Agt></Assgnr>
      <Assgne><Agt><FinInstnId><BICFI>${escXml(input.assignorBic)}</BICFI></FinInstnId></Agt></Assgne>
      <CreDtTm>${escXml(input.createdAt)}</CreDtTm>
    </Assgnmt>
    <RslvdCase><Id>${escXml(input.caseId)}</Id></RslvdCase>
    <Sts><Conf>${escXml(conf)}</Conf></Sts>
    <CxlDtls>
      <TxInfAndSts>
        <CxlStsId>${escXml(input.cxlStsId)}</CxlStsId>
        <OrgnlEndToEndId>${escXml(input.orgnlEndToEndId)}</OrgnlEndToEndId>
        <TxCxlSts>${escXml(txSts)}</TxCxlSts>
      </TxInfAndSts>
    </CxlDtls>
  </RsltnOfInvstgtn>
</Document>`;
}
